import { Text, View } from '@components/primitives';
import { NotificationFilterKey } from '@atoms/Notifications/NotificationFilters';
import colors from '@theme/colors';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import { ActivityIndicator, Pressable, SectionList, ViewToken } from 'react-native';
import { useTranslation } from 'react-i18next';
import { NotificationApiItem } from './notificationData';
import styles from './styles';

dayjs.extend(utc);

export interface NotificationItem {
  category: string;
  description: string;
  id: string;
  isRead: boolean;
  source: string;
  time: string;
  title: string;
}

interface NotificationSection {
  data: NotificationItem[];
  markAllRead?: boolean;
  title: string;
}

interface GetNotificationSectionsParams {
  language: string;
  notifications: NotificationApiItem[];
  now?: Date;
  selectedFilter: NotificationFilterKey;
  t: (key: string) => string;
}

interface NotificationListProps {
  isMarkAllReadDisabled?: boolean;
  isPaginating?: boolean;
  onMarkAllRead?: () => void;
  onReachEnd?: () => void;
  onScrollStart?: () => void;
  onVisibleItemsChanged?: (items: NotificationItem[]) => void;
  sections: NotificationSection[];
}

export const NOTIFICATION_TITLE_MAX_LENGTH = 80;
export const NOTIFICATION_DESCRIPTION_MAX_LENGTH = 150;

const filterCategoryMap: Record<NotificationFilterKey, NotificationApiItem['category'] | null> = {
  all: null,
  info: 'information',
  message: 'message',
};

export const getNotificationApiCategory = (
  selectedFilter: NotificationFilterKey,
): 'all' | 'information' | 'message' => {
  if (selectedFilter === 'info') return 'information';
  if (selectedFilter === 'message') return 'message';

  return 'all';
};

const formatNotificationTime = (createdAt: string): string => {
  return dayjs.utc(createdAt).local().format('HH:mm');
};

const getSectionKey = (createdAt: string): string => dayjs.utc(createdAt).local().format('YYYY-M-D');

export const truncateNotificationText = (value: string, maxLength: number): string => {
  const characters = Array.from(value);

  if (characters.length <= maxLength) return value;

  return `${characters.slice(0, maxLength).join('')}...`;
};

export const normalizeNotificationText = (value: string): string => {
  return value.replace(/(\\n|\/n|\r?\n)+/g, ' ').replace(/\s+/g, ' ').trim();
};

export const markAllReadPressHandler = (isDisabled: boolean, onMarkAllRead?: () => void) => (): void => {
  if (isDisabled) return;

  onMarkAllRead?.();
};

export const viewableItemsChangedHandler =
  (onVisibleItemsChanged?: (items: NotificationItem[]) => void) =>
  ({ viewableItems }: { viewableItems: Array<ViewToken> }): void => {
    const items = viewableItems
      .map(viewableItem => viewableItem.item as NotificationItem | null)
      .filter((item): item is NotificationItem => Boolean(item));

    onVisibleItemsChanged?.(items);
  };

export const filterNotifications = (
  notifications: NotificationApiItem[],
  selectedFilter: NotificationFilterKey,
): NotificationApiItem[] => {
  const category = filterCategoryMap[selectedFilter];

  if (!category) return notifications;

  return notifications.filter(item => {
    if (selectedFilter === 'info') return item.category === 'information' || item.category === 'informasi';

    return item.category === category;
  });
};

export const getNotificationSectionTitle = (
  createdAt: string,
  language: string,
  t: (key: string) => string,
  now: Date = new Date(),
): string => {
  const currentDate = dayjs(now).startOf('day');
  const notificationDate = dayjs.utc(createdAt).local();
  const dayDiff = currentDate.diff(notificationDate.startOf('day'), 'day');

  if (dayDiff === 0) return t('notifications.list.today');
  if (dayDiff === 1) return t('notifications.list.yesterday');

  return notificationDate.locale(language.startsWith('id') ? 'id' : 'en').format('dddd, D MMMM YYYY');
};

export const getNotificationSections = ({
  language,
  notifications,
  now = new Date(),
  selectedFilter,
  t,
}: GetNotificationSectionsParams): NotificationSection[] => {
  const filteredNotifications = filterNotifications(notifications, selectedFilter).sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );
  const sectionsMap = new Map<string, NotificationSection>();

  filteredNotifications.forEach(notification => {
    const sectionTitle = getNotificationSectionTitle(notification.createdAt, language, t, now);
    const sectionKey = getSectionKey(notification.createdAt);
    const localizedTitle = language.startsWith('id') ? notification.title_id : notification.title_en;
    const localizedDescription = language.startsWith('id') ? notification.body_id : notification.body_en;

    if (!sectionsMap.has(sectionKey)) {
      sectionsMap.set(sectionKey, {
        title: sectionTitle,
        data: [],
      });
    }

    sectionsMap.get(sectionKey)?.data.push({
      id: notification.id,
      title: truncateNotificationText(normalizeNotificationText(localizedTitle), NOTIFICATION_TITLE_MAX_LENGTH),
      description: truncateNotificationText(
        normalizeNotificationText(localizedDescription),
        NOTIFICATION_DESCRIPTION_MAX_LENGTH,
      ),
      time: formatNotificationTime(notification.createdAt),
      isRead: notification.isRead,
      category: notification.category,
      source: notification.source,
    });
  });

  const sections = Array.from(sectionsMap.values());

  if (sections[0]) sections[0].markAllRead = true;

  return sections;
};

function NotificationList({
  isMarkAllReadDisabled = false,
  isPaginating = false,
  onMarkAllRead,
  onReachEnd,
  onScrollStart,
  onVisibleItemsChanged,
  sections,
}: NotificationListProps): React.JSX.Element {
  const { t } = useTranslation('common');

  return (
    <SectionList
      contentContainerStyle={styles.listContentContainer}
      keyExtractor={item => item.id}
      renderItem={({ item }) => (
        <View style={styles.itemCard} testID={`notification-card-${item.id}`}>
          <View
            style={item.isRead ? styles.sectionDotPlaceholder : styles.sectionDot}
            testID={item.isRead ? `notification-read-dot-${item.id}` : `notification-unread-dot-${item.id}`}
          />
          <View style={styles.itemContainer}>
            <View style={styles.itemContent}>
              <View style={styles.itemHeader}>
                <Text
                  color={colors.neutral.charcoal}
                  includeFontPadding={false}
                  style={styles.itemTitle}
                  variant="md-bold">
                  {item.title}
                </Text>
                <Text color={colors.neutral.darkMist} style={styles.itemTime} variant="xsm-normal">
                  {item.time}
                </Text>
              </View>
              <Text
                color={colors.neutral.charcoal}
                includeFontPadding={false}
                style={styles.itemDescription}
                variant="sm-normal">
                {item.description}
              </Text>
            </View>
          </View>
        </View>
      )}
      renderSectionHeader={({ section }) => (
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle} variant="sm-normal">
            {section.title}
          </Text>
          {section.markAllRead ? (
            <Pressable
              disabled={isMarkAllReadDisabled}
              hitSlop={8}
              onPress={markAllReadPressHandler(isMarkAllReadDisabled, onMarkAllRead)}
              testID="notification-mark-all-read-button">
              <Text
                style={[styles.markAllReadText, isMarkAllReadDisabled ? styles.markAllReadTextDisabled : null]}
                variant="sm-normal">
                {t('notifications.list.markAllRead')}
              </Text>
            </Pressable>
          ) : null}
        </View>
      )}
      ListFooterComponent={
        isPaginating ? (
          <View style={styles.paginationLoaderContainer} testID="notification-list-pagination-loader">
            <ActivityIndicator color={colors.secondary.orchid} />
          </View>
        ) : null
      }
      onEndReached={() => {
        if (!isPaginating) onReachEnd?.();
      }}
      onEndReachedThreshold={0.3}
      onScrollBeginDrag={onScrollStart}
      onViewableItemsChanged={viewableItemsChangedHandler(onVisibleItemsChanged)}
      sections={sections}
      testID="notification-list"
      viewabilityConfig={{ itemVisiblePercentThreshold: 50 }}
    />
  );
}

export default NotificationList;
